import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogIn, UserPlus, ExternalLink, KeyRound, UserSearch, SearchCode,
  Search, Megaphone, Sparkles,
} from 'lucide-react';

/**
 * Right sidebar for the public landing page (Feb 2026 — Founder brief).
 *
 * Sections (top → bottom):
 *   1. Login / Register buttons — duplicates the top-header CTAs for visitors
 *      who scroll and lose sight of the header.
 *   2. Important Links — deep-links into existing flows (register, forgot
 *      password, tender search). "Find My Nodal Officer" is marked SOON until
 *      that surface exists.
 *   3. Tender Search box — small keyword search that routes to the existing
 *      `/tenders/public-search?q=…` endpoint (already runtime-filtered for
 *      demo/QA leakage).
 *   4. Notice / Announcements — public messaging block. Uses a static list of
 *      current platform announcements; can be swapped for an API-backed feed
 *      later without touching the layout.
 *
 * All routes reused — nothing new invented on the backend for this component.
 * Column collapses under the main content on `<lg` viewports.
 */
const IMPORTANT_LINKS = [
  { key: 'enrol',   label: 'Online Bidder Enrolment',      icon: UserPlus,   to: '/register?role=supplier' },
  { key: 'forgot',  label: 'Generate / Forgot Password',   icon: KeyRound,   to: '/forgot-password' },
  { key: 'nodal',   label: 'Find My Nodal Officer',        icon: UserSearch, soon: true },
  { key: 'search',  label: 'Search by ID / Title / Ref.',  icon: SearchCode, to: '/tenders/public-search' },
];

const ANNOUNCEMENTS = [
  {
    key: 'founding',
    dateISO: '2026-02-01',
    badge: 'OFFER',
    title: 'Founding Member Campaign — 100 slots open per role',
    body: 'First 100 buyers and 100 suppliers get discounted lifetime pricing. No end date — open until slots fill.',
  },
  {
    key: 'beta',
    dateISO: '2026-01-15',
    badge: 'BETA',
    title: 'ProcureOne Ultra is now in Public Beta',
    body: 'Register your society or vendor today. Live tenders, guided bidding, live negotiations already available.',
  },
  {
    key: 'gst',
    dateISO: '2026-02-08',
    badge: 'NEW',
    title: 'Monthly GST Report ready in Admin Reports',
    body: 'GSTR-1-ready CSV/PDF exports for every calendar month, with intra/inter-state tax split.',
  },
];

const fmtDate = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const SectionCard = ({ title, icon: Icon, children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={testid}>
    <div className="flex items-center gap-2 px-3 py-2.5 bg-[#0A1F44] text-white">
      <Icon className="w-4 h-4 text-[#D4AF37] shrink-0" />
      <h3 className="text-xs font-bold uppercase tracking-wider">{title}</h3>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

export default function PublicRightSidebar() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const submitSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    navigate(`/tenders/public-search?${params}`);
  };

  return (
    <aside
      className="space-y-4 lg:sticky lg:top-[92px] lg:h-fit"
      aria-label="Quick actions"
      data-testid="public-right-sidebar"
    >
      {/* ── Login / Register ─────────────────────────────── */}
      <div
        className="rounded-xl bg-gradient-to-br from-[#0A1F44] via-[#1e1b4b] to-[#5B21B6] text-white p-4 shadow-md"
        data-testid="right-sidebar-cta-card"
      >
        <div className="flex items-center gap-2 text-[10px] font-bold text-[#D4AF37] uppercase tracking-wider mb-2">
          <Sparkles className="w-3 h-3" /> Access Portal
        </div>
        <div className="text-sm text-slate-200 mb-3 leading-snug">
          Login to publish tenders, review bids and manage your society — or register in under 2 minutes.
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-white/30 text-sm font-semibold text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
            data-testid="right-sidebar-login-btn"
          >
            <LogIn className="w-4 h-4" /> Login
          </button>
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-[#D4AF37] text-white text-sm font-bold hover:bg-[#c19d2f] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            data-testid="right-sidebar-register-btn"
          >
            <UserPlus className="w-4 h-4" /> Register
          </button>
        </div>
      </div>

      {/* ── Important Links ──────────────────────────────── */}
      <SectionCard title="Important Links" icon={ExternalLink} testid="right-sidebar-links-card">
        <ul className="space-y-1">
          {IMPORTANT_LINKS.map((l) => {
            const Icon = l.icon;
            return (
              <li key={l.key}>
                <button
                  type="button"
                  onClick={() => !l.soon && navigate(l.to)}
                  disabled={l.soon}
                  className={
                    'w-full flex items-center gap-2 px-2 py-2 rounded-md text-left text-sm text-[#0A1F44] hover:bg-slate-50 hover:text-[#5B21B6] transition-colors ' +
                    (l.soon ? 'opacity-60 cursor-not-allowed' : '')
                  }
                  data-testid={`right-sidebar-link-${l.key}`}
                >
                  <Icon className="w-4 h-4 text-[#5B21B6] shrink-0" />
                  <span className="flex-1 leading-tight">{l.label}</span>
                  {l.soon && (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#0A1F44] border border-[#D4AF37]/50">
                      Soon
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </SectionCard>

      {/* ── Tender Search box ────────────────────────────── */}
      <SectionCard title="Tender Search" icon={Search} testid="right-sidebar-search-card">
        <form onSubmit={submitSearch} className="space-y-2">
          <label className="block text-[11px] font-semibold text-slate-600" htmlFor="right-tender-q">
            Search by keyword, category or location
          </label>
          <div className="flex gap-2">
            <input
              id="right-tender-q"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. Painting, Mumbai, Housekeeping"
              className="flex-1 min-w-0 px-3 py-2 rounded-md border border-slate-200 text-sm focus:border-[#5B21B6] focus:outline-none"
              data-testid="right-sidebar-search-input"
            />
            <button
              type="submit"
              className="inline-flex items-center justify-center px-3 py-2 rounded-md bg-[#0A1F44] text-white hover:bg-[#5B21B6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
              data-testid="right-sidebar-search-submit"
              aria-label="Search tenders"
            >
              <Search className="w-4 h-4" />
            </button>
          </div>
          <div className="text-[10px] text-slate-500">Public tenders only. No login required.</div>
        </form>
      </SectionCard>

      {/* ── Notice / Announcements ───────────────────────── */}
      <SectionCard title="Notices & Announcements" icon={Megaphone} testid="right-sidebar-notices-card">
        <ul className="space-y-3">
          {ANNOUNCEMENTS.map((a) => (
            <li key={a.key} className="border-l-2 border-[#D4AF37] pl-2.5" data-testid={`right-sidebar-notice-${a.key}`}>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#0A1F44] uppercase tracking-wider">
                  {a.badge}
                </span>
                <span className="text-[10px] text-slate-500">{fmtDate(a.dateISO)}</span>
              </div>
              <div className="text-sm font-semibold text-[#0A1F44] leading-snug">{a.title}</div>
              <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{a.body}</div>
            </li>
          ))}
        </ul>
      </SectionCard>
    </aside>
  );
}
